import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowLeft, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function PortalLogin() {
  const { login, user, ready } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) nav("/portal");
  }, [ready, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      nav("/portal");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[100svh] grid place-items-center bg-wcc-navy px-5 relative overflow-hidden wcc-noise">
      <img src="/assets/field-3.webp" alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
      <div className="absolute inset-0 bg-wcc-navy/70" />
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-widest text-white/50 hover:text-white mb-8 transition-colors" data-testid="login-back">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="bg-white p-8 sm:p-10 border-t-4 border-wcc-red">
          <img src="/assets/logo.png" alt="West Coral" className="h-14 w-auto mb-6" />
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-5 h-5 text-wcc-red" />
            <h1 className="font-display text-2xl font-semibold text-wcc-ink tracking-tight">Owner Dashboard</h1>
          </div>
          <p className="text-wcc-ink/60 mb-8">Sign in to view applications and onboarding forms.</p>
          <form onSubmit={submit} data-testid="login-form">
            <div className="space-y-5">
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email"
                  className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
              </div>
              <div>
                <label className="mono text-[10px] uppercase tracking-widest text-wcc-gray">Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password"
                  className="w-full bg-transparent border-b-2 border-wcc-ink/20 focus:border-wcc-red outline-none py-2.5 text-wcc-ink transition-colors" />
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-wcc-red text-white font-semibold px-6 py-4 rounded-sm hover:bg-wcc-red-deep disabled:opacity-60 transition-colors">
              {loading ? "Signing in…" : <>Sign In <LogIn className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
